import { useCallback, useEffect, useMemo, useState } from 'react';
import { TOP_POD_TITLES } from '../../../core/constants';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import { BASEFONTSIZE, PAGESIZE, REFETCHDELAY, RefetchOptions } from '../constants';
import { FilterForm, Settings, Sorting, SpinnerTabs } from '../../../composed-components';
import ResizeTable from '../../../composed-components/Resize';
import { EFontSize, TColumns } from '../types/types';
import Pagination from '../../../components/Pagination';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import { useUserPermit } from '../../../context/User.context';
import Legend from '../../../composed-components/Legend';
import { TableFooter } from '../../../components/Table';
import { useActions } from '../../../context/Actions.context';
import getDataSource from '../utils/getDataSource';
import { applyRoleBasedFilters } from '../utils/roleBasedFilters';
import { getTopPODTitles } from '../columns/getTopPodTitles';

const TopPODTitles = () => {
  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [page, setPage] = useState<number>(1);
  const [deleteItems, setDeleteItems] = useState<string[]>([]);
  const [isFilterInitialized, setIsFilterInitialized] = useState<boolean>(false);

  const { isActionLoading, setActionLoading, isRefreshed } = useActions();
  const { edit_titles, move_titles_hubspot, role, default_filters: userDefaultFilters } = useUserPermit();
  const { filter, setFilter } = useFilter();
  const { sort } = useSort();

  const changePage = p => setPage(p);
  const deleteBooks = book => setDeleteItems(book);
  const changeFontSize = size => setFontSize(size);

  const { data = [], isLoading, isFetching, refetch, dataRefreshTime, isBackendJobComplete } = useBooksList(
    TOP_POD_TITLES, {
      page,
      ...filter[TOP_POD_TITLES],
      ...sort[TOP_POD_TITLES]
    });

  const { count, refetch: refetchRowCount } = useDataRowCount(TOP_POD_TITLES, { ...filter[TOP_POD_TITLES] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch();
      refetchRowCount();
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortComponent = useCallback(
    (title: string) => (
      <Sorting id={TOP_POD_TITLES} title={title} changePage={changePage} />
    ),
    []
  );

  const thead = useMemo(() => getTopPODTitles(sortComponent, TOP_POD_TITLES),
    [edit_titles, move_titles_hubspot, sortComponent]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource = getDataSource(data);

  useEffect(() => {
    if (!isFilterInitialized) {
      if (!window.location.search) {
        const updatedFilters = applyRoleBasedFilters(filter, role, userDefaultFilters);

        setFilter(prevFilters => ({
          ...prevFilters,
          ...updatedFilters
        }));
      }

      setIsFilterInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!isRefreshed && isFilterInitialized) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }
  }, [page, filter[TOP_POD_TITLES], sort[TOP_POD_TITLES], isFilterInitialized]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm
          id={TOP_POD_TITLES}
          page={page}
          changePage={changePage}
          dataRefreshTime={dataRefreshTime}
          isBackendJobComplete={isBackendJobComplete}
        />
        <Settings
          thead={thead}
          id={TOP_POD_TITLES}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
          deleteBooks={deleteBooks}
          deleteItems={deleteItems}
        />
      </div>

      {!isLoading ? (
        <>
          <ResizeTable
            key={columns.map(col => col.key).join('-')}
            fontSize={fontSize}
            id={TOP_POD_TITLES}
            dataSource={dataSource}
            deleteItems={deleteItems}
            isFetching={isFetching || isActionLoading}
            deleteBooks={deleteBooks}
            columns={columns}
            refetchTable={refetchTable}
          />
          <TableFooter>
            <Legend map={['price']} />
            <Pagination
              total={count}
              current={page}
              onChange={setPage}
              pageSize={PAGESIZE}
            />
          </TableFooter>
        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default TopPODTitles;